import Graphic from "@arcgis/core/Graphic";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";
import { useContext, useEffect, useRef, useState } from "react";
import { AppConfig, LayersConfig } from "../../AppConfig";
import { AppContext, LighthouseContext, VersionContext } from "../../Context";
import {
    applyEdits,
    computeTrueNorth,
    findAndSelectFeature,
    getAllRelatedFeatures,
    GetCodedValueAndAlias,
    getMapView,
} from "../../utils/arcgisUtils";
import FeatureViewerAttributes from "./FeatureViewerAttributes";
import FeatureViewerTitle from "./FeatureViewerTitle";
import FeatureWorkOrderGroup from "./FeatureWorkOrders/FeatureWorkOrderGroup";

import Point from "@arcgis/core/geometry/Point";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import EditorViewModel from "@arcgis/core/widgets/Editor/EditorViewModel";
import type { EditorViewModelProperties } from "@arcgis/core/widgets/Editor/EditorViewModel";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import { useDispatch, useSelector } from "react-redux";
import { setHasUnsavedChanges, setLoading } from "../../store/appSlice";
import { setIsEditing, setIsSavingLighthouse } from "../../store/lighthouseSlice";
import { LighthouseData } from "../../utils/lighthouseConstants";
import {
    hasSectorGeometry,
    hideLighthouses,
    loadLighthouse,
    saveLighthouse,
    unhideLighthouses,
} from "../../utils/lighthouseUtils";
import { getNewStatus, NFSStatus, setNewStatusToFeature } from "../../utils/nfsUtils";
import { drawSectors } from "../MapTools/LightHouse/lighthouseDrawing";
import FeatureWarnings from "./FeatureWarnings/FeatureWarnings";
import NauticalImageButton from "./NauticalImage/NauticalImageButton";
import RelatedObjectGroup from "./RelatedObjects/RelatedObjectGroup";
import SectorDescriptionGroup from "./SectorDescriptionGroup/SectorDescriptionGroup";
import { FeatureClasses, NauticalClasses } from "../../constants/NauticalObjects";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { StoreState } from "../../store/rootReducer";
import { isDefaultVersion } from "../../utils/versioningUtils";
import type Relationship from "@arcgis/core/layers/support/Relationship";
import type GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";

interface FeatureViewerContentProps {
    isEditing: boolean;
    feature: Graphic | null | undefined;
    canMove: boolean;
    canBeSentToFdv: boolean;
    setCanBeSentToFdv: (canBeSentToFdv: boolean) => void;
    setHasEditedAttributes: (hasEditedAttributes: boolean) => void;
}

const FeatureViewerContent = (props: FeatureViewerContentProps) => {
    const { isEditing, feature, canMove, canBeSentToFdv, setCanBeSentToFdv } = props;

    const dispatch = useDispatch();

    const [editingGeometry, setEditingGeometry] = useState(false);
    const [featuresBeforeEdit, setFeaturesBeforeEdit] = useState<Graphic[]>([]);
    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);

    const [showAllAttributes, setShowAllAttributes] = useState<boolean>(false);
    const [secondaryFeature, setSecondaryFeature] = useState<Graphic | undefined>(undefined);
    const [relatedObjects, setRelatedObjects] = useState<{ relationship: Relationship; features: Graphic[] }[] | null>(
        null
    );

    const [canHaveFdvWorkOrders, setCanHaveFdvWorkOrders] = useState<boolean>(false);

    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const versionContext = useContext(VersionContext);
    const versionManagementRef = versionContext?.versionServiceRef.value;

    const isInDefaultVersion =
        versionManagementRef?.current && isDefaultVersion(activeVersion, versionManagementRef.current);
    const isAllowedToCreateWorkOrders =
        isInDefaultVersion &&
        (feature?.attributes["nfs_status"] === NFSStatus.PROJECTED_EXISTING ||
            feature?.attributes["nfs_status"] === NFSStatus.PROJECTED);

    const layerConfig = useRef<LayersConfig | null | undefined>(null);
    const editorVM = useRef<EditorViewModel | null | undefined>(null);

    const appContext = useContext(AppContext);
    const lighthouseContext = useContext(LighthouseContext);
    const lighthouseDataRef = lighthouseContext?.lighthouseDataRef.value;

    const setLighthouseData = (data: LighthouseData) => {
        lighthouseContext?.lighthouseData.set(data);
        lighthouseContext!.lighthouseDataRef.value.current = data;
    };

    const editGeometry = async () => {
        const view = getMapView();
        if (!view || !feature) return;

        dispatch(setLoading(true));
        dispatch(setHasUnsavedChanges(true));

        if (editorVM.current) {
            editorVM.current.activeWorkflow?.cancel();
            editorVM.current.destroy();
            editorVM.current = undefined;
        }
        const featureClone = feature.clone();
        featureClone.layer = feature.layer;
        setFeaturesBeforeEdit([featureClone]);
        editorVM.current = new EditorViewModel({
            view: view,
            sketchViewModel: new SketchViewModel({
                updateOnGraphicClick: false,
            }),
        } as EditorViewModelProperties);

        let featuresWithGeometry: Graphic[] = [];

        const featureHasSectorGeometry = await hasSectorGeometry(feature);

        if (featureHasSectorGeometry) {
            const lightLayer = view.map?.findLayerById("Lys") as FeatureLayer;
            await loadLighthouse(
                feature.attributes.objectid,
                lightLayer,
                setLighthouseData,
                appContext?.selectedFeature.set,
                (loadingStatus: boolean) => dispatch(setLoading(loadingStatus)),
                () => {},
                feature
            );

            const lighthouseEditingLayer = view.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;
            lighthouseEditingLayer.visible = true;
            const trueNorth = await computeTrueNorth(appContext?.selectedFeature.value?.geometry as Point);
            drawSectors({
                graphicsLayer: lighthouseEditingLayer,
                data: lighthouseDataRef!.current,
                setIsEditing: (value) => dispatch(setIsEditing(value)),
                sectorsToFill: [],
                opacity: 0.5,
                drawNumbers: false,
                trueNorthCorrection: trueNorth,
                overrideSectorWidth: false,
                centerFeature: feature,
            });
            await hideLighthouses([feature], showRemovedFeatures);
        }
        if (feature.geometry?.type === "point") {
            const relatedFeatures = await getAllRelatedFeatures(feature);
            const relationsWithGeometry = relatedFeatures.filter(
                (relation) => relation.features.length > 0 && relation.features[0].geometry
            );
            for (const relation of relationsWithGeometry) {
                const layer = view.map?.layers.find(
                    (l) => l.type === "feature" && (l as FeatureLayer).layerId === relation.relationship.relatedTableId
                ) as FeatureLayer;
                for (const element of relation.features) {
                    const features = await layer.queryFeatures({
                        where: `objectid = ${element.attributes.objectid}`,
                        outFields: ["*"],
                        returnGeometry: true,
                    });
                    const graphic = features.features[0].clone();
                    graphic.layer = layer;
                    setFeaturesBeforeEdit((prev) => [...prev, graphic]);
                    featuresWithGeometry.push(graphic);
                }
            }
        }

        await editorVM.current.startUpdateWorkflowAtFeatureEdit(feature);
        if (feature.geometry?.type !== "point") {
            editorVM.current.sketchViewModel?.update(editorVM.current.sketchViewModel.updateGraphics.toArray(), {
                tool: "reshape",
            });
        }
        setEditingGeometry(true);
        dispatch(setLoading(false));
        editorVM.current.sketchViewModel?.on("update", async (event) => {
            if (
                ["move-stop", "reshape-stop", "rotate-stop", "scale-stop", "vertex-remove"].includes(
                    event.toolEventInfo?.type ?? ""
                )
            ) {
                dispatch(setLoading(true));
                // debounce apply edit.
                const updateObject = event.graphics[0].clone();
                updateObject.layer = feature.layer;
                if (!updateObject) return;
                updateObject.attributes = {};
                updateObject.attributes["objectid"] = event.graphics[0].attributes["objectid"];
                updateObject.attributes["globalid"] = event.graphics[0].attributes["globalid"];
                updateObject.attributes["nfs_status"] = feature.attributes["nfs_status"];

                setNewStatusToFeature(updateObject, NFSStatus.PROJECTED_EXISTING);

                if (feature.geometry?.type === "point") {
                    for (const element of featuresWithGeometry) {
                        const elementClone = element.clone();
                        elementClone.layer = element.layer;
                        (elementClone.geometry as Point).x = (event.graphics[0].geometry as Point).x;
                        (elementClone.geometry as Point).y = (event.graphics[0].geometry as Point).y;
                        elementClone.layer = element.layer;
                        await applyEdits(element.layer as FeatureLayer, {
                            updateFeatures: [elementClone],
                        });
                    }
                    if (!(await hasSectorGeometry(feature))) {
                        await applyEdits(feature.layer as FeatureLayer, {
                            updateFeatures: [updateObject],
                        });
                    }
                }

                if (!lighthouseDataRef?.current || !featureHasSectorGeometry) {
                    dispatch(setLoading(false));
                    return;
                }

                const lighthouseEditingLayer = view.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;
                lighthouseEditingLayer.visible = true;

                lighthouseDataRef.current.center = event.graphics[0].geometry as Point;
                for (const sector of lighthouseDataRef.current.sectors) {
                    sector.nfs_status = getNewStatus(sector.nfs_status, NFSStatus.PROJECTED_EXISTING);
                    sector.nfs_status_line1 = getNewStatus(sector.nfs_status_line1, NFSStatus.PROJECTED_EXISTING);
                    sector.nfs_status_line2 = getNewStatus(sector.nfs_status_line2, NFSStatus.PROJECTED_EXISTING);
                }
                setNewStatusToFeature(feature, NFSStatus.PROJECTED_EXISTING);

                const trueNorth = await computeTrueNorth(appContext?.selectedFeature.value?.geometry as Point);
                await drawSectors({
                    graphicsLayer: lighthouseEditingLayer,
                    data: lighthouseDataRef.current,
                    setIsEditing: (value) => dispatch(setIsEditing(value)),
                    sectorsToFill: [],
                    opacity: 0.5,
                    drawNumbers: false,
                    trueNorthCorrection: trueNorth,
                    overrideSectorWidth: false,
                    centerFeature: feature,
                });
                dispatch(setLoading(false));
            }
        });
    };

    const saveGeometry = async () => {
        dispatch(setLoading(true));
        dispatch(setHasUnsavedChanges(false));
        await editorVM.current?.activeWorkflow?.commit();
        editorVM.current?.destroy();
        editorVM.current = undefined;
        if (!feature) return;
        findAndSelectFeature(feature.layer as FeatureLayer, feature, (graphic) => {
            appContext?.selectedFeature.set(graphic);
        });
        setFeaturesBeforeEdit([]);
        setEditingGeometry(false);

        if (!lighthouseDataRef?.current || !(await hasSectorGeometry(feature))) {
            dispatch(setLoading(false));
            return;
        }

        const trueNorth = await computeTrueNorth(appContext?.selectedFeature.value?.geometry as Point);
        setNewStatusToFeature(feature, NFSStatus.PROJECTED_EXISTING);
        await saveLighthouse(
            lighthouseDataRef.current,
            appContext?.selectedFeature.set,
            feature,
            trueNorth,
            (savingLighthouseStatus: boolean) => dispatch(setIsSavingLighthouse(savingLighthouseStatus)),
            (loadingStatus: boolean) => dispatch(setLoading(loadingStatus))
        );
        unhideLighthouses(showRemovedFeatures);
        dispatch(setLoading(false));
    };

    const cancelGeometry = async () => {
        dispatch(setHasUnsavedChanges(false));
        dispatch(setLoading(true));
        if (editorVM.current) {
            editorVM.current.activeWorkflow?.cancel();
            editorVM.current.destroy();
            editorVM.current = undefined;
        }
        for (const featureBeforeEdit of featuresBeforeEdit) {
            await applyEdits(featureBeforeEdit.layer as FeatureLayer, { updateFeatures: [featureBeforeEdit] });
        }

        if (!feature) return;
        findAndSelectFeature(feature.layer as FeatureLayer, feature, (graphic) => {
            appContext?.selectedFeature.set(graphic);
        });

        setFeaturesBeforeEdit([]);
        setEditingGeometry(false);
        unhideLighthouses(showRemovedFeatures);
        dispatch(setLoading(false));
    };

    const featureRefForRelated = useRef<Graphic | null | undefined>(feature);

    const setPotentialSecondaryFeature = (
        theFeature: Graphic | null | undefined,
        related: { relationship: Relationship; features: Graphic[] }[]
    ) => {
        if (!theFeature || (theFeature?.layer as FeatureLayer).layerId !== FeatureClasses.Lys) {
            return;
        }

        if (theFeature.attributes.klasse === NauticalClasses.LB_Lysboeye) {
            findAndSetSecondary("FlytendeMerke");
        } else if (theFeature.attributes.klasse === NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning) {
            findAndSetSecondary("IB");
        } else if (
            theFeature.attributes.klasse === NauticalClasses.FL_Fyrlykt ||
            theFeature.attributes.klasse === NauticalClasses.LA_Lanterne
        ) {
            findAndSetSecondary("IB", false);
        } else {
            setSecondaryFeature(undefined);
        }

        function findAndSetSecondary(relationshipName: string, mandatory: boolean = true): void {
            const relatedSecondary = related.find((rel) => rel.relationship.name === relationshipName);
            if (relatedSecondary && relatedSecondary.features.length > 0) {
                const f = relatedSecondary.features[0];
                console.log("Found secondary feature via relationship ", relationshipName, f);
                const view = getMapView();
                f.layer = view!.map!.layers.find(
                    (l) =>
                        l.type === "feature" &&
                        (l as FeatureLayer).layerId === relatedSecondary.relationship.relatedTableId
                ) as FeatureLayer;
                setSecondaryFeature(f);
            } else if (mandatory) {
                console.warn(`Expected to find related ${relationshipName} but none found`);
                setSecondaryFeature(undefined);
            }
        }
    };

    useEffect(() => {
        if (featureRefForRelated.current !== feature) {
            setRelatedObjects(null);
            featureRefForRelated.current = feature;
        }

        if (feature) {
            getAllRelatedFeatures(feature)
                .then((related) => {
                    if (featureRefForRelated.current !== feature) {
                        return;
                    }
                    setRelatedObjects(related);
                    setPotentialSecondaryFeature(feature, related);
                })
                .catch((error) => {
                    console.error("Error fetching related features", error);
                });
        } else {
            setRelatedObjects(null);
        }
        if (editingGeometry) {
            cancelGeometry();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature]);

    const nauticalClassesThatCannotBeSentToFdv = new Set([NauticalClasses.OV_Overett]);
    const featureClassesThatCanBeSentToFdv = new Set([
        FeatureClasses.Lys,
        FeatureClasses.IB,
        FeatureClasses.Fastsjømerke,
        FeatureClasses.Flytendemerke,
        FeatureClasses.Racon,
    ]);

    useEffect(() => {
        if (!relatedObjects || !feature) {
            setCanBeSentToFdv(false);
            setCanHaveFdvWorkOrders(false);
            return;
        }

        // Racons and IB (HIB) related to a light are handled through the parent light object
        const hasRelatedLys = relatedObjects.some((ro) => ro.relationship.name === "Lys");

        const canFdv =
            Object.hasOwn(feature.attributes, "gis_obj_id") &&
            Object.hasOwn(feature.attributes, "klasse") &&
            featureClassesThatCanBeSentToFdv.has((feature.layer as FeatureLayer).layerId as number) &&
            !nauticalClassesThatCannotBeSentToFdv.has(feature.attributes.klasse) &&
            !hasRelatedLys;
        setCanBeSentToFdv(canFdv);
        setCanHaveFdvWorkOrders(canFdv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature, relatedObjects]);

    useEffect(() => {
        if (!feature) return;

        symbolUtils.getDisplayedSymbol(feature).then((symbol) => {
            if (symbol) {
                symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                    if (!symbolElement) return;
                    setSymbolHtmlElement(symbolElement);
                });
            } else {
                setSymbolHtmlElement(null);
            }
        });

        layerConfig.current = AppConfig.Layers.find((layerConfig) => layerConfig.Name === feature?.layer?.id);
        const featureObjectIds = featuresBeforeEdit.map((f) => f.getObjectId());
        if (feature === null || !featureObjectIds.includes(feature?.getObjectId())) {
            setEditingGeometry(false);
            setFeaturesBeforeEdit([]);
            if (editorVM.current) {
                editorVM.current.activeWorkflow?.cancel();
                editorVM.current.destroy();
                editorVM.current = undefined;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature]);

    useEffect(() => {
        return () => {
            if (editorVM.current) {
                editorVM.current.activeWorkflow?.cancel();
                editorVM.current.destroy();
                editorVM.current = undefined;
            }
        };
    }, []);

    if (!feature) return null;

    return (
        <div className="feature-viewer-content">
            <div className="feature-viewer-feature-box">
                <div className="flex-col w-100 space-between">
                    <FeatureViewerTitle
                        symbolHtmlElement={symbolHtmlElement}
                        feature={feature}
                        layerConfig={layerConfig}
                        goTo
                        editingGeometry={editingGeometry}
                        editGeometry={editGeometry}
                        isEditing={isEditing}
                        saveGeometry={saveGeometry}
                        cancelGeometry={cancelGeometry}
                        canMove={canMove}
                    />
                </div>
                {feature.attributes[layerConfig.current?.StatusField ?? "status"] !== undefined && (
                    <div className="feature-status-panel">
                        {
                            GetCodedValueAndAlias(
                                feature.layer,
                                layerConfig.current?.StatusField ?? "",
                                feature.attributes[layerConfig.current?.StatusField ?? "status"]
                            )[1]
                        }
                    </div>
                )}
                <FeatureViewerAttributes
                    isEditing={isEditing}
                    showAllAttributes={showAllAttributes}
                    feature={feature}
                    canBeSentToFdv={canBeSentToFdv}
                    secondaryFeature={secondaryFeature}
                    setHasEditedAttributes={props.setHasEditedAttributes}
                />
                <NfsButton
                    onClick={() => {
                        setShowAllAttributes(!showAllAttributes);
                    }}
                >
                    {showAllAttributes ? "Vis mindre" : "Vis mer"}
                </NfsButton>
                <NauticalImageButton feature={feature} />

                <SectorDescriptionGroup feature={feature} />
                <RelatedObjectGroup isEditing={isEditing} relatedObjects={relatedObjects} />
                <FeatureWorkOrderGroup
                    feature={feature}
                    isEditing={isEditing || isAllowedToCreateWorkOrders}
                    canBeSentToFdv={canBeSentToFdv}
                    canHaveFdvWorkOrders={canHaveFdvWorkOrders}
                />
            </div>
            <FeatureWarnings feature={feature} />
        </div>
    );
};

export default FeatureViewerContent;
